/**
 * The applicant frame: Bewerbungen + Unterlagen (COL-533).
 *
 * Until now both applicant tabs rendered a "no booking yet" card and a link
 * back to the classic dashboard — the design's applicant screen existed only
 * in the replica. This is it against real rows.
 *
 * There is no `applications` table: an application IS a `bookings` row that
 * has not reached a contract yet, and the four-step rail is derived in
 * `tenant-application-stage.ts` from the journey's own contract predicates
 * rather than from a second reading of `contract_status`.
 */

import { Link } from "react-router-dom";
import { Check, Plus } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTenantApplications } from "@/hooks/useTenantApplications";
import {
  useTenantDocumentSummary,
  useUploadTenantDocuments,
} from "@/hooks/useTenantDocumentSummary";
import {
  DOCUMENT_ACCEPT_ATTRIBUTE,
  IDENTITY_DOCUMENT_TYPE,
  INCOME_DOCUMENT_TYPE,
  type TenantDocumentType,
} from "@/lib/tenant-document-upload";
import { translateDictionary } from "@/lib/i18n/dictionary";
import { baseLanguageFor, localeFor } from "@/lib/i18n/languages";
import { getImageUrl } from "@/lib/imageUrl";
import { parseDateOnly } from "@/lib/roomAvailability";
import { firstImageUrl } from "@/lib/roomImages";
import {
  APPLICATION_STEPS,
  applicationStepIndex,
  type ApplicationStep,
} from "@/lib/tenant-application-stage";
import { getTenantDashboardV2Labels } from "@/lib/tenant-dashboard-v2-copy";
import { formatTenantDate } from "@/lib/date-format";

function Rail({
  current,
  labels,
}: {
  current: number;
  labels: Record<ApplicationStep, string>;
}) {
  return (
    <ol className="grid w-full grid-cols-4 gap-3 sm:max-w-md">
      {APPLICATION_STEPS.map((step, i) => {
        const reached = i <= current;
        return (
          <li key={step} className="min-w-0">
            {/*
              The rail is a list of labelled segments, not four bare divs: a
              screen reader otherwise reads the step names with no indication
              of which one the application is actually on.
            */}
            <div
              aria-hidden
              className={[
                "h-0.5 w-full rounded-full",
                reached ? "bg-foreground" : "bg-border",
              ].join(" ")}
            />
            <p
              className={[
                "mt-2 truncate text-xs sm:text-sm",
                i === current
                  ? "font-semibold text-foreground"
                  : reached
                    ? "text-foreground/70"
                    : "text-muted-foreground",
              ].join(" ")}
              aria-current={i === current ? "step" : undefined}
            >
              {labels[step]}
            </p>
          </li>
        );
      })}
    </ol>
  );
}

export default function TenantApplications() {
  const { tDict, language: activeLanguage } = useLanguage();
  const language = baseLanguageFor(activeLanguage);
  const locale = localeFor(activeLanguage);
  const d = translateDictionary(
    "tenant.dashboardV2",
    getTenantDashboardV2Labels(language),
    (key, literal) => tDict(key, literal ?? "", activeLanguage)
  );

  const {
    data: applications = [],
    isPending,
    isError,
  } = useTenantApplications();
  const docs = useTenantDocumentSummary();
  // Pending and failed both come back empty; neither means "nothing uploaded".
  const docsUnknown = docs.isPending || docs.isError;

  const { upload, uploadingType } = useUploadTenantDocuments();

  const addFiles = async (
    documentType: TenantDocumentType,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = Array.from(event.target.files ?? []);
    // Clearing the input lets the same file be picked again after a failure.
    event.target.value = "";
    try {
      const result = await upload(documentType, files, d.documentsUploadFailed);
      // Per-file failures do not throw; say so rather than claim success.
      result.errors?.forEach((e) => toast.error(`${e.fileName}: ${e.error}`));
    } catch (err) {
      toast.error((err as Error)?.message || d.documentsUploadFailed);
    }
  };

  const stepLabels: Record<ApplicationStep, string> = {
    submitted: d.stepSubmitted,
    in_review: d.stepInReview,
    offer: d.stepOffer,
    contract: d.stepContract,
  };

  const money = (value: unknown) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return "—";
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(n);
  };

  // `parseDateOnly`, not `new Date`: a date-only column parses as UTC midnight
  // and reads back as the previous day anywhere west of UTC.
  const day = (iso: unknown) => {
    const raw = String(iso ?? "");
    if (!raw) return null;
    const date = parseDateOnly(raw);
    return Number.isNaN(date.getTime()) ? null : formatTenantDate(date);
  };

  if (isPending) {
    return (
      <Card className="p-6">
        <p className="text-muted-foreground">{d.loading}</p>
      </Card>
    );
  }
  // A failed list must not read as "you have applied to nothing" — that is the
  // most alarming possible way to be wrong on this screen.
  if (isError) {
    return (
      <Card className="p-6">
        <p className="text-destructive">{d.loadFailed}</p>
      </Card>
    );
  }

  return (
    <div className="space-y-10">
      <section>
        <h2 className="mb-4 text-lg font-semibold text-foreground">
          {d.applicationsHeading}
        </h2>

        {applications.length === 0 ? (
          <Card className="space-y-4 p-6">
            <p className="text-muted-foreground">{d.applicationsEmpty}</p>
            <Button asChild>
              <Link to="/properties">{d.applicationsFindRoom}</Link>
            </Button>
          </Card>
        ) : (
          <Card className="divide-y divide-border/60 p-0">
            {applications.map((booking: any) => {
              const room = booking?.rooms;
              const property = room?.properties;
              const raw =
                firstImageUrl(room?.images) ?? property?.image_url ?? null;
              const photo = raw ? getImageUrl(raw, { width: 240 }) : null;
              /*
                Two different dates, and the distinction is the point.

                An AGREED move-in ("ab 01.09.") only exists once a booking
                carries one. A fresh application has neither date, and the
                design still shows when the room frees up ("frei ab 01.11.")
                -- which is the ROOM's availability, not a commitment to this
                applicant. Showing the room's date under "ab" would read as a
                move-in that has been agreed when nothing has.
              */
              const agreedFrom = day(
                booking?.move_in_date ?? booking?.start_date
              );
              const roomFree = agreedFrom
                ? null
                : day(room?.available_from ?? room?.next_available_date);
              const price = money(booking?.monthly_rent ?? room?.price);
              const detail = agreedFrom
                ? d.allInFrom
                    .replace("{price}", price)
                    .replace("{date}", agreedFrom)
                : roomFree
                  ? d.allInFree
                      .replace("{price}", price)
                      .replace("{date}", roomFree)
                  : d.allIn.replace("{price}", price);

              return (
                <div
                  key={String(booking.id)}
                  className="flex flex-col gap-5 p-5 sm:flex-row sm:items-center sm:justify-between sm:gap-8"
                >
                  <div className="flex min-w-0 items-center gap-4">
                    {photo ? (
                      <img
                        src={photo}
                        alt=""
                        loading="lazy"
                        className="h-16 w-20 flex-none rounded-lg object-cover"
                      />
                    ) : (
                      <div className="h-16 w-20 flex-none rounded-lg bg-muted" />
                    )}
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-foreground">
                        {room?.title
                          ? d.roomAt
                              .replace("{room}", room.title)
                              .replace("{address}", property?.title ?? "")
                              .replace(/\s*·\s*$/, "")
                          : (property?.title ?? "—")}
                      </p>
                      <p className="mt-1 truncate text-sm text-muted-foreground">
                        {detail}
                      </p>
                    </div>
                  </div>
                  <Rail
                    current={applicationStepIndex(booking)}
                    labels={stepLabels}
                  />
                </div>
              );
            })}
          </Card>
        )}
      </section>

      {/* Where the dashboard search's document results land (COL-623). */}
      <section data-search-target="applications-documents">
        <h2 className="text-lg font-semibold text-foreground">
          {d.documentsHeading}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {docs.isError
            ? d.loadFailed
            : docs.isPending
              ? d.loading
              : d.documentsProgress
                  .replace("{done}", String(docs.uploadedKinds))
                  .replace("{total}", String(docs.totalKinds))}
        </p>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          {[
            {
              key: "identity" as const,
              title: d.documentsIdentity,
              hint: d.documentsIdentityHint,
              files: docs.identityFiles,
              type: IDENTITY_DOCUMENT_TYPE,
            },
            {
              key: "income" as const,
              title: d.documentsIncome,
              hint: d.documentsIncomeHint,
              files: docs.incomeFiles,
              type: INCOME_DOCUMENT_TYPE,
            },
          ].map((slot) => {
            /*
              Absent evidence is not an empty pack: the hook returns an empty
              list while pending and after a failure, so treating "no files"
              as "nothing uploaded" told an applicant whose ID is on file that
              it was not. Unknown renders neither state.
            */
            const done = !docsUnknown && slot.files.length > 0;
            return (
              <Card
                key={slot.key}
                className={[
                  "p-5",
                  done
                    ? "border-transparent bg-muted"
                    : "border-dashed bg-transparent",
                ].join(" ")}
              >
                <div className="flex items-start gap-3">
                  <span
                    aria-hidden
                    className={[
                      "mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full",
                      done
                        ? "bg-foreground text-background"
                        : "bg-muted text-muted-foreground",
                    ].join(" ")}
                  >
                    {done ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Plus className="h-4 w-4" />
                    )}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-foreground">
                      {slot.title}
                    </p>
                    <p className="mt-0.5 text-sm text-muted-foreground">
                      {docsUnknown
                        ? docs.isError
                          ? d.loadFailed
                          : d.loading
                        : done
                          ? slot.files.length === 1
                            ? d.documentsOneFile
                            : d.documentsFilesCount.replace(
                                "{count}",
                                String(slot.files.length)
                              )
                          : slot.hint}
                    </p>
                  </div>
                </div>

                {done ? (
                  <ul className="mt-3 space-y-2">
                    {slot.files.map((file) => (
                      <li
                        key={file.id}
                        className="truncate rounded-xl bg-background px-3 py-2 text-sm text-foreground"
                        title={file.file_name ?? file.title ?? ""}
                      >
                        {file.file_name ?? file.title ?? "—"}
                      </li>
                    ))}
                  </ul>
                ) : null}

                {/*
                  Adding goes through the SHARED `uploadTenantDocuments`, which
                  V1's own mutation calls too — one writer, so the two
                  dashboards cannot drift apart on what an upload means.

                  Removing is not offered here on purpose. `tenant_documents`
                  has no DELETE policy for users, so a client delete matches
                  zero rows and reports success; that needs an RPC.

                  Until it exists the card asks the tenant to request removal
                  from HELP, not from the settings screen: V1's delete there
                  removes the stored file and then fails silently on the row,
                  so pointing at it would hand them the one path that cannot
                  do what it promises. Support reaches an admin, who does hold
                  the policy.
                */}
                {/*
                  `sr-only`, NOT `hidden`. A display:none input is out of the
                  tab order entirely, so the control was unreachable by
                  keyboard and invisible to a screen reader — the label is not
                  focusable on its own. Visually hidden keeps it focusable and
                  announced, and `focus-within` puts the ring on the label the
                  sighted user actually sees.
                */}
                <label
                  className={[
                    "mt-3 inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-border px-3 py-2 text-sm font-semibold text-foreground",
                    "focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
                    uploadingType === slot.type
                      ? "pointer-events-none opacity-60"
                      : "",
                  ].join(" ")}
                >
                  <input
                    type="file"
                    multiple
                    accept={DOCUMENT_ACCEPT_ATTRIBUTE}
                    className="sr-only"
                    disabled={uploadingType !== null}
                    onChange={(e) => addFiles(slot.type, e)}
                  />
                  {uploadingType === slot.type ? (
                    d.documentsUploading
                  ) : (
                    <>
                      <Plus className="h-4 w-4" aria-hidden />
                      {done ? d.documentsAddFile : d.documentsAddFirstFile}
                    </>
                  )}
                </label>
                <p className="mt-2 text-xs text-muted-foreground">
                  {d.documentsAccepted}
                </p>
              </Card>
            );
          })}
        </div>

        <p className="mt-4 text-sm text-muted-foreground">
          {d.documentsRemoveElsewhere}
        </p>
        <Button asChild variant="outline" className="mt-2">
          <Link to="/dashboard?dashboard=v1&settings=documents">
            {d.documentsManage}
          </Link>
        </Button>
      </section>
    </div>
  );
}
